import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { deleteProductVarient, fetchProductVarientsList } from "../../shared/productChildMastersApi";
import { IconPlus } from "../../shared/toolbarIcons";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

export function ProductVarientsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [iRes, pRes] = await Promise.all([fetchProductVarientsList(), apiGet("/api/Products")]);
    if (!iRes.ok) {
      setError(iRes.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(iRes.items);
    }
    if (pRes.ok) setProducts(pRes.data);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const onEdit = (x) => {
    navigate(`/product-varients?edit=${x.id}`);
  };

  const onDelete = async (id) => {
    if (!(await window.confirmDialog("Delete this product varient?"))) return;
    const res = await deleteProductVarient(id);
    if (!res.ok) setError(res.error ?? "Failed to delete product varient");
    else await load();
  };

  const productById = useMemo(() => {
    const map = new Map();
    for (const product of products) map.set(product.id, product);
    return map;
  }, [products]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        item,
        productName: productById.get(item.productId)?.name || `#${item.productId}`,
        sku: item.skU ?? "-",
        price: item.price ?? 0,
        qty: item.balanceQuantity ?? 0,
        active: item.isActive ? "Yes" : "No"
      })),
    [items, productById]
  );

  const total = rows.length;
  const columns = [
      { field: "productName", headerName: "Product", flex: 1, minWidth: 150 },
      { field: "sku", headerName: "SKU", flex: 1, minWidth: 130 },
      { field: "price", headerName: "Price", width: 110 },
      { field: "qty", headerName: "Qty", width: 100 },
      { field: "active", headerName: "Active", width: 100 },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="table-actions">
            <button className="icon-button" type="button" title="Edit" onClick={() => onEdit(params.row.item)}>
              ✏️
            </button>
            <button className="icon-button danger" type="button" title="Delete" onClick={() => void onDelete(params.row.item.id)}>
              🗑️
            </button>
          </div>
        )
      }
  ];

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Product Varients</h2>
          <p>{loading ? "Loading..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/product-varients" className="icon-button icon-button--toolbar" title="Add product varient" aria-label="Add product varient">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
